import {
  completeTenantAdministrationSetup,
  markTenantCompetenceSetupChoiceDialogSeen,
  removeTenantLogo,
  updateCurrentTenant,
  updateTenantSchoolLevels,
  uploadTenantLogo,
} from '@/api/resources/tenant.api';
import { useRefreshSetupChecklist } from '@/composables/setupChecklist/useRefreshSetupChecklist';
import { useToast } from '@/composables/useToast';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { currentTenantKeys } from './currentTenant.keys';

const useTenantSetupMutations = (refresh: () => Promise<void>) => {
  const queryClient = useQueryClient();
  const { showError } = useToast();
  const { refreshSetupChecklist } = useRefreshSetupChecklist();
  const completeSetup = useMutation({
    mutationFn: completeTenantAdministrationSetup,
    onSuccess: async () => {
      await refresh();
      await refreshSetupChecklist();
    },
    onError: (error) => showError(error),
  });
  const markSetupSeen = useMutation({
    mutationFn: markTenantCompetenceSetupChoiceDialogSeen,
    onSuccess: (tenant) => queryClient.setQueryData(currentTenantKeys.detail(tenant.id), tenant),
    onError: (error) => showError(error),
  });
  return {
    completeAdministrationSetup: completeSetup.mutateAsync,
    markCompetenceSetupChoiceDialogSeen: async (): Promise<void> => {
      await markSetupSeen.mutateAsync();
    },
  };
};

export const useCurrentTenantMutation = () => {
  const queryClient = useQueryClient();
  const { editSuccessToast, showError } = useToast();
  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: currentTenantKeys.all }),
      queryClient.invalidateQueries({ queryKey: currentTenantKeys.ownersAndAdmins }),
    ]);
  };
  const editOptions = {
    onSuccess: async (): Promise<void> => {
      await refresh();
      editSuccessToast();
    },
    onError: (error: Error): void => showError(error),
  };
  const editTenant = useMutation({ mutationFn: updateCurrentTenant, ...editOptions });
  const uploadLogo = useMutation({ mutationFn: uploadTenantLogo, ...editOptions });
  const removeLogo = useMutation({ mutationFn: removeTenantLogo, ...editOptions });
  const updateSchoolLevels = useMutation({ mutationFn: updateTenantSchoolLevels, ...editOptions });
  const setupMutations = useTenantSetupMutations(refresh);

  return {
    editCurrentTenant: editTenant.mutateAsync,
    uploadLogo: uploadLogo.mutateAsync,
    removeLogo: removeLogo.mutateAsync,
    isUploadingLogo: uploadLogo.isPending,
    updateSchoolLevels: async (levels: number[]): Promise<void> => {
      if (!levels.length) return;
      await updateSchoolLevels.mutateAsync(levels as [number, ...number[]]);
    },
    ...setupMutations,
    refresh,
  };
};
